import { ReferentialKey } from 'api/referential';
import { ComponentKey } from 'enums/componentKey';
import { InputsConfig } from 'types/form/inputsConfig';
import { SpecialConditionsFormFieldNames } from './specialConditionForm';
import { toDayInput } from 'core/utils/dates';

type FieldNames = typeof SpecialConditionsFormFieldNames;

export const specialConditionsInputsConfig: InputsConfig<FieldNames[keyof FieldNames]> =
  {
    [SpecialConditionsFormFieldNames.BankAccountId]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankAccountId,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.BANK_ACCOUNT',
      type: 'text',
      disabled: true
    },

    [SpecialConditionsFormFieldNames.FrequencyCode]: {
      componentKey: ComponentKey.ReferentialDropdown,
      referentialKey: ReferentialKey.MandateFrequency,
      name: SpecialConditionsFormFieldNames.FrequencyCode,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.FREQUENCY',
      required: true
    },

    [SpecialConditionsFormFieldNames.StartDate]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.StartDate,
      type: 'date',
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.START_DATE',
      min: toDayInput(new Date()),
      max: (() => { const d = new Date(); d.setMonth(d.getMonth() + 6); return toDayInput(d); })()
    },

    [SpecialConditionsFormFieldNames.ExistingOutstanding]: {
      componentKey: ComponentKey.InputWithUnit,
      name: SpecialConditionsFormFieldNames.ExistingOutstanding,
      type: 'number',
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.EXISTING_OUTSTANDING',
      labelTooltip: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.TOOLTIPS.EXISTING_OUTSTANDING',
      placeholder: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.EXISTING_OUTSTANDING',
      unit: '€'
    },

    [SpecialConditionsFormFieldNames.ExistingOutstandingInsurer]: {
      componentKey: ComponentKey.InputWithUnit,
      name: SpecialConditionsFormFieldNames.ExistingOutstandingInsurer,
      type: 'number',
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.EXISTING_OUTSTANDING_INSURER',
      labelTooltip: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.TOOLTIPS.EXISTING_OUTSTANDING_INSURER',
      placeholder: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.EXISTING_OUTSTANDING_INSURER',
      unit: '€'
    },

    [SpecialConditionsFormFieldNames.CurrentLoanNumber]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.CurrentLoanNumber,
      type: 'text',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.CURRENT_LOAN_NUMBER',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.CURRENT_LOAN_NUMBER'
    },

    // Bank contact
    [SpecialConditionsFormFieldNames.BankEntityName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankEntityName,
      type: 'text',
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.ENTITY_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.ENTITY_NAME'
    },

    [SpecialConditionsFormFieldNames.BankEntityAddressSearch]: {
      componentKey: ComponentKey.GoogleLocation,
      name: SpecialConditionsFormFieldNames.BankEntityAddressSearch,
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.ENTITY_ADDRESS',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.ENTITY_ADDRESS',
      fieldPrefix: 'bankContact.entityAddress'
    },

    [SpecialConditionsFormFieldNames.BankEntityAdditionalAddress]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankEntityAdditionalAddress,
      type: 'text',
      required: false,
      label: 'ACTOR_FORM.ADDRESS.LABELS.ADDITIONAL_ADDRESS',
      placeholder: 'ACTOR_FORM.ADDRESS.PLACEHOLDERS.ADDITIONAL_ADDRESS'
    },

    [SpecialConditionsFormFieldNames.BankLastName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankLastName,
      type: 'text',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.COMMERCIAL_LAST_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.COMMERCIAL_LAST_NAME'
    },

    [SpecialConditionsFormFieldNames.BankFirstName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankFirstName,
      type: 'text',
      label:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.COMMERCIAL_FIRST_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.COMMERCIAL_FIRST_NAME'
    },

    [SpecialConditionsFormFieldNames.BankPhone]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankPhone,
      type: 'tel',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.COMMERCIAL_PHONE',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.COMMERCIAL_PHONE'
    },

    [SpecialConditionsFormFieldNames.BankEmail]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.BankEmail,
      type: 'email',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.COMMERCIAL_EMAIL',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.COMMERCIAL_EMAIL'
    },

    // Insurance contact (RENEG_COURTIER only)
    [SpecialConditionsFormFieldNames.InsuranceEntityName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsuranceEntityName,
      type: 'text',
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_BROKER_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_BROKER_NAME'
    },

    [SpecialConditionsFormFieldNames.InsuranceEntityAddressSearch]: {
      componentKey: ComponentKey.GoogleLocation,
      name: SpecialConditionsFormFieldNames.InsuranceEntityAddressSearch,
      required: true,
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_BROKER_ADDRESS',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_BROKER_ADDRESS',
      fieldPrefix: 'insuranceContact.entityAddress'
    },

    [SpecialConditionsFormFieldNames.InsuranceEntityAdditionalAddress]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsuranceEntityAdditionalAddress,
      type: 'text',
      required: false,
      label: 'ACTOR_FORM.ADDRESS.LABELS.ADDITIONAL_ADDRESS',
      placeholder: 'ACTOR_FORM.ADDRESS.PLACEHOLDERS.ADDITIONAL_ADDRESS'
    },

    [SpecialConditionsFormFieldNames.InsuranceLastName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsuranceLastName,
      type: 'text',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_CONTACT_LAST_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_CONTACT_LAST_NAME'
    },

    [SpecialConditionsFormFieldNames.InsuranceFirstName]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsuranceFirstName,
      type: 'text',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_CONTACT_FIRST_NAME',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_CONTACT_FIRST_NAME'
    },

    [SpecialConditionsFormFieldNames.InsurancePhone]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsurancePhone,
      type: 'tel',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_CONTACT_PHONE',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_CONTACT_PHONE'
    },

    [SpecialConditionsFormFieldNames.InsuranceEmail]: {
      componentKey: ComponentKey.InputText,
      name: SpecialConditionsFormFieldNames.InsuranceEmail,
      type: 'email',
      label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.INSURANCE_CONTACT_EMAIL',
      placeholder:
        'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.INSURANCE_CONTACT_EMAIL'
    }
  };
